import { Field, inputCls, primaryBtnCls } from "@/components/Kit";
import { PhoneInput } from "@/components/PhoneInput";

export const CATEGORIES = [
  ["distributor", "Distributor"], ["stockist", "Stockist"], ["wholesaler", "Wholesaler"],
  ["manufacturer", "Manufacturer"], ["importer", "Importer"], ["c_and_f", "C&F agent"],
  ["local_vendor", "Local vendor"], ["other", "Other"],
];

export const LICENCE_TYPES = ["20B", "21B", "20", "21", "20C", "21C", "20D", "21D",
  "wholesale", "retail", "other"];

export const emptySupplier = () => ({
  name: "", contact_person: "", phone: "", alt_phone: "", email: "", address: "", city: "",
  state: "", country: "India", pincode: "", gstin: "", pan: "", drug_license_no: "",
  drug_license_type: "20B", drug_license_expiry: "", category: "distributor",
  payment_terms_days: "0", credit_limit: "0", bank_name: "", account_holder: "",
  account_no: "", ifsc: "", upi_id: "", notes: "",
});

export const toSupplierForm = (s) => {
  const f = emptySupplier();
  const picked = Object.fromEntries(Object.keys(f).map((k) => [k, s[k] ?? f[k]]));
  return {
    ...picked,
    payment_terms_days: String(s.payment_terms_days ?? 0),
    credit_limit: String(s.credit_limit ?? 0),
    drug_license_type: s.drug_license_type || "20B",
    category: s.category || "distributor",
    country: s.country || "India",
  };
};

const Section = ({ title, children }) => (
  <div>
    <p className="overline text-[#EA580C] mb-4">{title}</p>
    <div className="grid gap-5 sm:grid-cols-3">{children}</div>
  </div>
);

export function SupplierForm({ form, setForm, onSubmit, busy, error, editing }) {
  const set = (patch) => setForm({ ...form, ...patch });
  return (
    <form onSubmit={onSubmit} className="space-y-8" data-testid="supplier-form">
      <Section title="Business identity">
        <Field label="Supplier / business name *" className="sm:col-span-2">
          <input required minLength={2} className={inputCls} value={form.name}
            data-testid="supplier-name-input" onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="Supplier category">
          <select className={inputCls} value={form.category} data-testid="supplier-category-select"
            onChange={(e) => set({ category: e.target.value })}>
            {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Contact person">
          <input className={inputCls} value={form.contact_person}
            data-testid="supplier-contact-input" onChange={(e) => set({ contact_person: e.target.value })} />
        </Field>
        <div><PhoneInput label="Mobile" value={form.phone} testid="supplier-phone"
          onChange={(v) => set({ phone: v })} /></div>
        <div><PhoneInput label="Alternate phone" value={form.alt_phone} testid="supplier-alt-phone"
          onChange={(v) => set({ alt_phone: v })} /></div>
        <Field label="Email">
          <input type="email" className={inputCls} value={form.email} data-testid="supplier-email-input"
            onChange={(e) => set({ email: e.target.value })} />
        </Field>
      </Section>

      <Section title="Address">
        <Field label="Address" className="sm:col-span-3">
          <input className={inputCls} value={form.address} data-testid="supplier-address-input"
            onChange={(e) => set({ address: e.target.value })} />
        </Field>
        <Field label="City">
          <input className={inputCls} value={form.city} data-testid="supplier-city-input"
            onChange={(e) => set({ city: e.target.value })} />
        </Field>
        <Field label="State">
          <input className={inputCls} value={form.state} data-testid="supplier-state-input"
            onChange={(e) => set({ state: e.target.value })} />
        </Field>
        <Field label="Country">
          <input className={inputCls} value={form.country} data-testid="supplier-country-input"
            onChange={(e) => set({ country: e.target.value })} />
        </Field>
        <Field label="PIN code">
          <input className={inputCls} value={form.pincode} data-testid="supplier-pincode-input"
            onChange={(e) => set({ pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} />
        </Field>
      </Section>

      <Section title="Statutory">
        <Field label="GSTIN">
          <input className={inputCls} value={form.gstin} data-testid="supplier-gstin-input"
            placeholder="27AAACT2727Q1ZW"
            onChange={(e) => set({ gstin: e.target.value.toUpperCase().slice(0, 15) })} />
        </Field>
        <Field label="PAN">
          <input className={inputCls} value={form.pan} data-testid="supplier-pan-input"
            placeholder="ABCDE1234F"
            onChange={(e) => set({ pan: e.target.value.toUpperCase().slice(0, 10) })} />
        </Field>
        <Field label="Drug licence no.">
          <input className={inputCls} value={form.drug_license_no} data-testid="supplier-licence-input"
            onChange={(e) => set({ drug_license_no: e.target.value.toUpperCase() })} />
        </Field>
        <Field label="Drug licence type">
          <select className={inputCls} value={form.drug_license_type} data-testid="supplier-licence-type-select"
            onChange={(e) => set({ drug_license_type: e.target.value })}>
            {LICENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Drug licence expiry">
          <input type="date" className={inputCls} value={(form.drug_license_expiry || "").slice(0, 10)}
            data-testid="supplier-licence-expiry-input"
            onChange={(e) => set({ drug_license_expiry: e.target.value })} />
        </Field>
      </Section>

      <Section title="Commercial terms">
        <Field label="Payment terms (credit days)">
          <input type="number" min="0" className={inputCls} value={form.payment_terms_days}
            data-testid="supplier-terms-input" onChange={(e) => set({ payment_terms_days: e.target.value })} />
        </Field>
        <Field label="Credit limit (₹)">
          <input type="number" min="0" className={inputCls} value={form.credit_limit}
            data-testid="supplier-credit-limit-input" onChange={(e) => set({ credit_limit: e.target.value })} />
        </Field>
      </Section>

      <Section title="Bank & payment details">
        <Field label="Bank name">
          <input className={inputCls} value={form.bank_name} data-testid="supplier-bank-input"
            onChange={(e) => set({ bank_name: e.target.value })} />
        </Field>
        <Field label="Account holder name">
          <input className={inputCls} value={form.account_holder} data-testid="supplier-account-holder-input"
            onChange={(e) => set({ account_holder: e.target.value })} />
        </Field>
        <Field label="Account number">
          <input className={inputCls} value={form.account_no} data-testid="supplier-account-no-input"
            onChange={(e) => set({ account_no: e.target.value.replace(/\D/g, "").slice(0, 20) })} />
        </Field>
        <Field label="IFSC">
          <input className={inputCls} value={form.ifsc} data-testid="supplier-ifsc-input"
            placeholder="HDFC0001234"
            onChange={(e) => set({ ifsc: e.target.value.toUpperCase().slice(0, 11) })} />
        </Field>
        <Field label="UPI / payment ID">
          <input className={inputCls} value={form.upi_id} data-testid="supplier-upi-input"
            onChange={(e) => set({ upi_id: e.target.value })} />
        </Field>
      </Section>

      <Section title="Notes">
        <Field label="Internal notes" className="sm:col-span-3">
          <textarea rows={3} className={inputCls} value={form.notes} data-testid="supplier-notes-input"
            onChange={(e) => set({ notes: e.target.value })} />
        </Field>
      </Section>

      {error && <p className="text-sm font-medium text-red-600" data-testid="supplier-form-error">{error}</p>}
      <button disabled={busy} className={primaryBtnCls} data-testid="supplier-submit-btn">
        {editing ? "Update supplier" : "Save supplier"}
      </button>
    </form>
  );
}
